import { useEffect, useState } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';

import { ErrorMessage } from '@hookform/error-message';

import { Label } from 'components/Label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/Select';
import { Flex, Grid } from 'components/layout';
import { Typography } from 'components/typography';
import { useFieldAttributes, useLoadContext } from 'hooks/useLoadContext';
import useTMSContext from 'hooks/useTMSContext';
import { LoadTextInput } from 'pages/LoadView/LoadInformation/Components';
import {
  getCommodityOptions,
  getTransportTypeOptions,
} from 'pages/QuoteView/LoadBuilding/McleodSectionForms/helpers';
import { IntegrationCore } from 'types/Integration';
import { Load, NormalizedLoad } from 'types/Load';
import { getFieldAttribute, initFieldAttributes } from 'types/LoadAttributes';
import { TransportType } from 'types/QuoteRequest';
import { TMS } from 'types/enums/Integrations';
import { resolveTransportType } from 'utils/loadInfoAndBuilding';

export function SpecificationsForm({
  load,
  formMethods,
}: {
  load: Load | NormalizedLoad;
  formMethods: UseFormReturn<NormalizedLoad>;
}) {
  const {
    control,
    formState: { errors },
    setValue,
    getValues,
  } = formMethods;
  const { loadAttrsObj: attrs } = useLoadContext();
  const specs = load.specifications;
  const { tmsName, tenant, tmsID } = useTMSContext();
  const [isMcleodTMS, setIsMcleodTMS] = useState<boolean>(
    tmsName === TMS.McleodEnterprise
  );

  const isAljexTMS = tmsName === TMS.Aljex;
  const isTaiTMS = tmsName === TMS.Tai;

  const allFieldAttrs = useFieldAttributes();

  const getFieldAttr = (fieldName: string) =>
    getFieldAttribute(allFieldAttrs, fieldName) ?? initFieldAttributes;
  const transportTypeAttr = getFieldAttr('specifications.transportType');

  // Safety check to ensure context is properly initialized
  if (!allFieldAttrs || allFieldAttrs.length === 0) {
    return <div>Loading specifications...</div>;
  }

  useEffect(() => {
    setIsMcleodTMS(tmsName === TMS.McleodEnterprise);
  }, [tmsName]);

  useEffect(() => {
    if (!isMcleodTMS && specs.transportType !== undefined) {
      const currentFormValue = getValues('specifications.transportType');
      const resolvedValueFromSpecs = resolveTransportType(
        specs.transportType ?? null
      );

      if (currentFormValue !== resolvedValueFromSpecs) {
        setValue('specifications.transportType', resolvedValueFromSpecs ?? '', {
          shouldDirty: false,
        });
      }
    }
  }, [specs.transportType, isMcleodTMS, getValues, setValue]);

  // Field support checks - common fields across all TMS
  const isWeightSupported = !attrs.specifications.totalWeight?.isNotSupported;
  const isVolumeSupported = !attrs.specifications.totalVolume?.isNotSupported;
  const isDistanceSupported =
    !attrs.specifications.totalDistance?.isNotSupported;
  const isPalletSupported =
    !attrs.specifications.totalOutPalletCount?.isNotSupported;
  const isPiecesSupported = !attrs.specifications.totalPieces?.isNotSupported;
  const isPiecesTypeSupported =
    !attrs.specifications.totalPiecesType?.isNotSupported;

  const isPiecesTypeTrackedAsUnit =
    load.specifications.totalPiecesType &&
    load.specifications.totalPieces?.unit;

  const transportTypeOptions = isMcleodTMS
    ? getTransportTypeOptions({
        id: tmsID,
        name: tmsName,
        tenant,
      } as IntegrationCore)
    : undefined;

  const commodityOptions = isMcleodTMS
    ? getCommodityOptions({
        id: tmsID,
        name: tmsName,
        tenant,
      } as IntegrationCore)
    : undefined;

  return (
    <Flex direction='col' gap='md' className='w-full'>
      {/* Transport Type and Size row */}
      <Grid cols='2' gap='sm' className='w-full'>
        {/* Transport Type - always shown if supported */}
        {!transportTypeAttr.isNotSupported && (
          <>
            {!isMcleodTMS ? (
              <div>
                <Label name={'specifications.transportType'}>
                  Transport Type
                </Label>
                <Controller
                  name='specifications.transportType'
                  control={control}
                  rules={{ required: false }}
                  render={({ field }) => {
                    return (
                      <Select
                        name={field.name}
                        onValueChange={field.onChange}
                        value={field.value}
                        disabled={transportTypeAttr.isReadOnly}
                      >
                        <SelectTrigger
                          name={field.name}
                          className='w-full mt-1'
                        >
                          <SelectValue placeholder='Choose' />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.values(TransportType)
                            .filter(
                              // Filter out transport types that are not supported by the TMS
                              (value) =>
                                value !== TransportType.SPECIAL &&
                                value !== TransportType.HOTSHOT
                            )
                            .map((option: TransportType) => {
                              return (
                                <SelectItem key={option} value={option}>
                                  {option === TransportType.TRUCKLOAD
                                    ? 'Truckload'
                                    : option}
                                </SelectItem>
                              );
                            })}
                        </SelectContent>
                      </Select>
                    );
                  }}
                />
                <ErrorMessage
                  errors={errors}
                  name={'specifications.transportType'}
                  render={({ message }: { message: string }) => (
                    <Typography variant='body-xs' className='text-error-500'>
                      {message}
                    </Typography>
                  )}
                />
              </div>
            ) : (
              <div>
                <Label name={'specifications.transportType'}>
                  Transport Type
                </Label>
                <Controller
                  name='specifications.transportType'
                  control={control}
                  rules={{ required: false }}
                  render={({ field }) => (
                    <Select
                      showSearch
                      name={field.name}
                      className='h-8'
                      placeholder={'Choose'}
                      optionFilterProp='children'
                      onChange={field.onChange}
                      value={field.value}
                      disabled={transportTypeAttr.isReadOnly}
                      options={transportTypeOptions?.map((type) => ({
                        value: type.label,
                        label: type.label,
                      }))}
                    />
                  )}
                />
                <ErrorMessage
                  errors={errors}
                  name={'specifications.transportType'}
                  render={({ message }: { message: string }) => (
                    <Typography variant='body-xs' className='text-error-500'>
                      {message}
                    </Typography>
                  )}
                />
              </div>
            )}
          </>
        )}

        {/* Empty for TAI (Units shown with Quantity below), empty for others (Units shown in 3-col grid) */}
        <div />
      </Grid>

      {/* Quantity and Units row - for TAI only, below Transport Type */}
      {isTaiTMS && (
        <Grid cols='2' gap='sm' className='w-full'>
          {isPiecesSupported ? (
            <LoadTextInput
              name='specifications.totalPieces.val'
              label={`Quantity`}
              inputType='number'
              options={{ valueAsNumber: true }}
            />
          ) : (
            <div />
          )}

          {isPiecesTypeSupported ? (
            <LoadTextInput
              name={`specifications.${isPiecesTypeTrackedAsUnit ? 'totalPiecesType' : 'totalPieces.unit'}`}
              label='Units'
            />
          ) : (
            <div />
          )}
        </Grid>
      )}

      {/* Commodity, Quantity, and Units row */}
      <Grid cols='3' gap='sm' className='w-full'>
        {/* Commodity/Item */}
        {!getFieldAttr('specifications.commodities').isNotSupported ? (
          <>
            {isMcleodTMS ? (
              <div>
                <Label name={'specifications.commodities'}>Commodity</Label>
                <Controller
                  name='specifications.commodities'
                  control={control}
                  rules={{ required: false }}
                  render={({ field }) => (
                    <Select
                      showSearch
                      name={field.name}
                      className='h-8'
                      placeholder={'Choose'}
                      optionFilterProp='children'
                      onChange={field.onChange}
                      value={field.value}
                      options={commodityOptions?.map((type) => ({
                        value: type.code,
                        label: type.description,
                      }))}
                    />
                  )}
                />
                <ErrorMessage
                  errors={errors}
                  name={'specifications.commodities'}
                  render={({ message }: { message: string }) => (
                    <Typography variant='body-xs' className='text-error-500'>
                      {message}
                    </Typography>
                  )}
                />
              </div>
            ) : (
              <LoadTextInput name='specifications.commodities' label='Item' />
            )}
          </>
        ) : (
          <div />
        )}

        {/* Quantity - show here for all TMS except TAI (TAI shows it above) */}
        {!isTaiTMS && isPiecesSupported ? (
          <LoadTextInput
            name='specifications.totalPieces.val'
            label={`Quantity`}
            inputType='number'
            options={{ valueAsNumber: true }}
          />
        ) : (
          <div />
        )}

        {/* Units - show here for all TMS except TAI (TAI shows it with Transport Type above) */}
        {!isTaiTMS && isPiecesTypeSupported ? (
          <>
            <LoadTextInput
              name={`specifications.${isPiecesTypeTrackedAsUnit ? 'totalPiecesType' : 'totalPieces.unit'}`}
              label='Units'
            />
          </>
        ) : (
          <div />
        )}
      </Grid>

      {/* Weight fields */}
      <Flex direction='col' gap='md'>
        {isWeightSupported && (
          <LoadTextInput
            name='specifications.totalWeight.val'
            label={`Gross Weight${specs.totalWeight?.unit ? ' (' + specs.totalWeight?.unit + ')' : ''}`}
            inputType='number'
            options={{ valueAsNumber: true }}
          />
        )}
      </Flex>

      {/* Volume */}
      {isVolumeSupported && (
        <LoadTextInput
          name='specifications.totalVolume.val'
          label={`Volume${specs.totalVolume?.unit ? ' (' + specs.totalVolume?.unit + ')' : ''}`}
          inputType='number'
          options={{ valueAsNumber: true }}
        />
      )}

      {/* Additional fields row */}
      <Grid cols='2' gap='sm' className='w-full'>
        {isDistanceSupported ? (
          <LoadTextInput
            name='specifications.totalDistance.val'
            label={`Distance${specs.totalDistance?.unit ? ' (' + specs.totalDistance?.unit + ')' : ''}`}
            inputType='number'
            options={{ valueAsNumber: true }}
          />
        ) : (
          <div />
        )}

        {isPalletSupported ? (
          <div className={isAljexTMS ? 'col-span-2' : ''}>
            <LoadTextInput
              name='specifications.totalOutPalletCount'
              label='Pallets'
              inputType='number'
              options={{ valueAsNumber: true }}
              readOnly={isAljexTMS}
            />
          </div>
        ) : (
          <div />
        )}
      </Grid>
    </Flex>
  );
}
